export const SHOP_MANAGEMENT_UNSAVED_CHANGES_GROUP = 'shop-management-unsaved-changes';

export const PRIMEVUE_EDITOR_OVERLAY_SELECTOR = [
  '.p-autocomplete-overlay',
  '.p-cascadeselect-overlay',
  '.p-datepicker-panel',
  '.p-menu',
  '.p-multiselect-overlay',
  '.p-popover',
  '.p-select-overlay',
  '.p-tieredmenu',
  '.p-treeselect-overlay',
].join(',');

const normalizeSnapshotValue = (value, seen = new WeakSet()) => {
  if (value === undefined) {
    return { __bayhiveType: 'undefined' };
  }

  if (typeof value === 'number' && !Number.isFinite(value)) {
    return { __bayhiveType: 'number', value: String(value) };
  }

  if (value === null || typeof value !== 'object') {
    return value;
  }

  if (value instanceof Date) {
    return { __bayhiveType: 'date', value: value.toISOString() };
  }

  if (seen.has(value)) {
    return { __bayhiveType: 'circular' };
  }

  seen.add(value);

  if (Array.isArray(value)) {
    const normalized = value.map((item) => normalizeSnapshotValue(item, seen));
    seen.delete(value);
    return normalized;
  }

  const normalized = Object.keys(value)
    .sort()
    .reduce((result, key) => {
      const item = value[key];

      if (typeof item !== 'function' && typeof item !== 'symbol') {
        result[key] = normalizeSnapshotValue(item, seen);
      }

      return result;
    }, {});

  seen.delete(value);
  return normalized;
};

export const createUnsavedChangesSnapshot = (value) =>
  JSON.stringify(normalizeSnapshotValue(value));

const controlledIdsForSurface = (surface) =>
  new Set(
    Array.from(surface?.querySelectorAll?.('[aria-controls], [aria-owns]') || []).flatMap(
      (control) =>
        [control.getAttribute('aria-controls'), control.getAttribute('aria-owns')]
          .filter(Boolean)
          .flatMap((value) => value.trim().split(/\s+/)),
    ),
  );

export const eventTargetBelongsToEditorSurface = (target, surface) => {
  if (!target || !surface || typeof target.closest !== 'function') {
    return false;
  }

  if (surface.contains(target)) {
    return true;
  }

  const overlay = target.closest(PRIMEVUE_EDITOR_OVERLAY_SELECTOR);

  if (!overlay) {
    return false;
  }

  const controlledIds = controlledIdsForSurface(surface);

  if (overlay.id && controlledIds.has(overlay.id)) {
    return true;
  }

  return Array.from(overlay.querySelectorAll('[id]')).some((element) =>
    controlledIds.has(element.id),
  );
};

export const editorSurfaceHasOpenOverlay = (surface) =>
  Array.from(surface?.querySelectorAll?.('[aria-expanded="true"]') || []).some((control) => {
    const controlledIds = [control.getAttribute('aria-controls'), control.getAttribute('aria-owns')]
      .filter(Boolean)
      .flatMap((value) => value.trim().split(/\s+/));

    return controlledIds.some((id) => {
      const controlledElement = document.getElementById(id);

      return Boolean(
        controlledElement?.matches?.(PRIMEVUE_EDITOR_OVERLAY_SELECTOR) ||
          controlledElement?.closest?.(PRIMEVUE_EDITOR_OVERLAY_SELECTOR),
      );
    });
  });

export const createUnsavedChangesController = ({
  confirm,
  group = SHOP_MANAGEMENT_UNSAVED_CHANGES_GROUP,
} = {}) => {
  let baseline = createUnsavedChangesSnapshot(undefined);
  let baselineInitialized = false;
  let explicitlyChanged = false;
  let pendingRequest = null;

  const begin = (value) => {
    baseline = createUnsavedChangesSnapshot(value);
    baselineInitialized = true;
    explicitlyChanged = false;
  };

  const markChanged = () => {
    explicitlyChanged = true;
  };

  const markClean = (value) => {
    begin(value);
  };

  const isDirty = (value) =>
    explicitlyChanged || (baselineInitialized && createUnsavedChangesSnapshot(value) !== baseline);

  const requestClose = ({
    currentValue,
    onLeave,
    header = 'Discard unsaved changes?',
    message = 'You have unsaved changes. If you leave now, they will be lost.',
  } = {}) => {
    if (!isDirty(currentValue)) {
      onLeave?.();
      return Promise.resolve(true);
    }

    if (pendingRequest) {
      return pendingRequest;
    }

    pendingRequest = new Promise((resolve) => {
      let settled = false;

      const finish = (accepted) => {
        if (settled) {
          return;
        }

        settled = true;
        pendingRequest = null;
        resolve(accepted);
      };

      confirm.require({
        group,
        header,
        message,
        icon: 'fa-solid fa-triangle-exclamation',
        acceptLabel: 'Discard changes',
        rejectLabel: 'Keep editing',
        acceptIcon: 'fa-solid fa-trash-can',
        rejectIcon: 'fa-solid fa-arrow-left',
        defaultFocus: 'reject',
        modal: true,
        blockScroll: true,
        acceptProps: {
          severity: 'danger',
          size: 'small',
        },
        rejectProps: {
          severity: 'secondary',
          size: 'small',
          variant: 'outlined',
        },
        accept: () => {
          markClean(currentValue);
          onLeave?.();
          finish(true);
        },
        reject: () => finish(false),
        onHide: () => finish(false),
      });
    });

    return pendingRequest;
  };

  return {
    begin,
    isDirty,
    markChanged,
    markClean,
    requestClose,
  };
};
